import * as React from "react";
import { flattenSkillsByPriority } from "../catalog";
import {
  ensureSkillsCatalog,
  getCachedSkillsCatalog,
  getSkillsCatalogStoreVersion,
  refreshSkillsCatalog,
  subscribeToSkillsCatalog,
} from "../catalogStore";
import type { SkillsCatalog, SkillOrigin, SkillSummary, SkillViewStatus } from "../types";

export interface UseSkillsCatalogOptions {
  /** When true, taskpane focus/visibility refetches even inside the cooldown. */
  forceRefreshOnFocus?: boolean;
}

export interface UseSkillsCatalogResult {
  status: SkillViewStatus;
  catalog: SkillsCatalog | undefined;
  skills: Array<SkillSummary & { origin: SkillOrigin }>;
  error: Error | null;
}

/**
 * Thin React adapter around the user-scoped shared catalog store.
 * This hook never reads a host or feature-flag service.
 */
export function useSkillsCatalog(
  options?: UseSkillsCatalogOptions
): UseSkillsCatalogResult {
  const forceRefreshOnFocus = Boolean(options?.forceRefreshOnFocus);
  const storeVersion = React.useSyncExternalStore(
    subscribeToSkillsCatalog,
    getSkillsCatalogStoreVersion,
    () => 0
  );
  const [requestState, setRequestState] = React.useState<{
    status: SkillViewStatus;
    error: Error | null;
  }>({ status: "loading", error: null });
  const catalog = getCachedSkillsCatalog();

  const [previousStoreVersion, setPreviousStoreVersion] = React.useState(storeVersion);
  if (previousStoreVersion !== storeVersion) {
    setPreviousStoreVersion(storeVersion);
    setRequestState({ status: "loading", error: null });
  }

  React.useEffect(() => {
    if (catalog) return undefined;

    let cancelled = false;
    void ensureSkillsCatalog()
      .then(() => {
        if (!cancelled) {
          setRequestState({ status: "ready", error: null });
        }
      })
      .catch((error: unknown) => {
        if (!cancelled) {
          setRequestState({
            status: "error",
            error: error instanceof Error ? error : new Error("Unable to load Skills."),
          });
        }
      });
    return () => {
      cancelled = true;
    };
  }, [catalog, storeVersion]);

  React.useEffect(() => {
    const refreshIfVisible = () => {
      if (document.visibilityState !== "visible") return;
      void refreshSkillsCatalog({ force: forceRefreshOnFocus }).catch(() => undefined);
    };

    window.addEventListener("focus", refreshIfVisible);
    document.addEventListener("visibilitychange", refreshIfVisible);
    return () => {
      window.removeEventListener("focus", refreshIfVisible);
      document.removeEventListener("visibilitychange", refreshIfVisible);
    };
  }, [forceRefreshOnFocus]);

  return {
    status: catalog ? "ready" : requestState.status,
    catalog,
    skills: catalog ? flattenSkillsByPriority(catalog.personal, catalog.directory) : [],
    error: catalog ? null : requestState.error,
  };
}
